/**
 * Swallow browser / iOS swipe-back while a modal is open: each overlay pushes a
 * dummy history entry so Back closes the top overlay instead of leaving the page.
 */

type OverlayEntry = {
    id: number;
    onClose: () => void;
};

const HISTORY_FLAG = "__c8Overlay";

/** Drop overlay bookkeeping so a later `pushState` is not mistaken for the overlay entry. */
export function copyHistoryStateWithoutOverlayFlag(state: unknown): Record<string, unknown> {
    if (!state || typeof state !== "object") return {};
    const { [HISTORY_FLAG]: _overlayFlag, ...rest } = state as Record<string, unknown>;
    return rest;
}

let nextId = 1;
const stack: OverlayEntry[] = [];
let skipPopCount = 0;
let listening = false;
let listenerWindow: Window | null = null;

function historyApi(): History | null {
    if (typeof window === "undefined" || !window.history?.pushState) return null;
    return window.history;
}

function onPopState(): void {
    if (skipPopCount > 0) {
        skipPopCount -= 1;
        return;
    }
    const top = stack.pop();
    if (!top) return;
    try {
        top.onClose();
    } catch {
        /* overlay close must not break history handling */
    }
}

function ensureListener(): void {
    if (typeof window === "undefined") return;
    if (listening && listenerWindow === window) return;
    if (listening && listenerWindow && listenerWindow !== window) {
        listenerWindow.removeEventListener("popstate", onPopState);
    }
    window.addEventListener("popstate", onPopState);
    listening = true;
    listenerWindow = window;
}

/**
 * Absorb the next N `popstate` events without closing an overlay entry.
 * Use when another system (e.g. the profile modal) calls `history.go(-N)` and a
 * follow-up overlay may already be on the stack — otherwise that pop closes it.
 */
export function ignoreNextModalHistoryPops(count: number): void {
    const n = Math.max(0, Math.floor(Number(count) || 0));
    if (n <= 0) return;
    ensureListener();
    skipPopCount += n;
}

/** Register an open overlay. Returns a disposer for when it closes without Back. */
export function acquireModalHistoryEntry(onClose: () => void): () => void {
    const history = historyApi();
    if (!history) return () => undefined;

    ensureListener();
    const id = nextId;
    nextId += 1;
    stack.push({ id, onClose });
    history.pushState({ ...copyHistoryStateWithoutOverlayFlag(history.state), [HISTORY_FLAG]: id }, "");

    return () => releaseModalHistoryEntry(id);
}

function releaseModalHistoryEntry(id: number): void {
    const idx = stack.findIndex((entry) => entry.id === id);
    if (idx < 0) return;

    const wasTop = idx === stack.length - 1;
    stack.splice(idx, 1);

    const history = historyApi();
    if (!wasTop || !history) return;
    const flag = (history.state as { [HISTORY_FLAG]?: unknown } | null)?.[HISTORY_FLAG];
    if (flag !== id) return;

    skipPopCount += 1;
    history.back();
}

/**
 * Run `cb` after any in-flight overlay `history.back()` has been applied.
 * Opening another overlay (profile) in the same turn as release races that pop
 * and the new overlay is discarded.
 */
export function whenModalHistoryIdle(cb: () => void): () => void {
    let cancelled = false;
    const run = () => {
        if (cancelled) return;
        cancelled = true;
        window.removeEventListener("popstate", onPop);
        window.clearTimeout(fallbackId);
        cb();
    };
    const onPop = () => {
        window.setTimeout(() => {
            if (skipPopCount > 0) return;
            run();
        }, 0);
    };
    let fallbackId = 0;
    if (typeof window === "undefined") {
        cb();
        return () => undefined;
    }
    window.addEventListener("popstate", onPop);
    // Wait when a release is in flight (`skipPopCount`) or still registered (`stack`) —
    // callers often close an overlay then navigate; React effect cleanup runs `history.back()`
    // after this call, and a 0ms navigate can be popped on slower mobile WebViews.
    const waitMs = stack.length > 0 || skipPopCount > 0 ? 100 : 0;
    fallbackId = window.setTimeout(run, waitMs);
    return () => {
        cancelled = true;
        window.removeEventListener("popstate", onPop);
        window.clearTimeout(fallbackId);
    };
}

/** Test-only: drop listeners and in-memory stack. */
export function resetModalHistoryBackForTests(): void {
    stack.length = 0;
    skipPopCount = 0;
    nextId = 1;
    if (listening && listenerWindow) {
        listenerWindow.removeEventListener("popstate", onPopState);
    }
    listening = false;
    listenerWindow = null;
}
